import { useUser } from '@descope/react-sdk';
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import {
  DEFAULT_TENANT_ID,
  TenantId,
  isCartesianEmail,
  readStoredTenant,
  storeTenant,
} from '@/features/tenant/lib/tenant';

type TenantContextValue = {
  tenant: TenantId;
  setTenant: (tenant: TenantId) => void;
};

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

export function TenantProvider({ children }: { children: ReactNode }) {
  const { user, isUserLoading } = useUser();
  const [selectedTenant, setSelectedTenant] = useState<TenantId>(() =>
    readStoredTenant(),
  );

  // Only Cartesian users may use a non-default tenant (e.g. `demo`); the backend
  // rejects others with a 401. Eligibility is unknown until the user loads, so
  // until then we treat them as ineligible to avoid sending a stale `demo`.
  const isEligible =
    !isUserLoading && !!user?.email && isCartesianEmail(user.email);

  // Derive the tenant actually sent to the backend rather than mutating state:
  // a non-eligible user can never send a stale persisted value, even if one
  // lingers in localStorage from a previous (eligible) session.
  const tenant = isEligible ? selectedTenant : DEFAULT_TENANT_ID;

  // Once we know the user is ineligible, clear any stale persisted selection so
  // it can't be picked up again on the next load.
  useEffect(() => {
    if (!isUserLoading && !isEligible) {
      storeTenant(DEFAULT_TENANT_ID);
    }
  }, [isUserLoading, isEligible]);

  const setTenant = useCallback(
    (next: TenantId) => {
      // Don't persist a selection the user isn't allowed to make.
      if (!isEligible) return;
      storeTenant(next);
      setSelectedTenant(next);
    },
    [isEligible],
  );

  return (
    <TenantContext.Provider value={{ tenant, setTenant }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant(): TenantContextValue {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}
